// src/pages/Tickets/TicketReopenReasonList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Ticket Reopen Reasons (Ticket_Reopen_Reason_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via ticketReopenReasonService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { ticketReopenReasonService, projectService } from '../../api';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

export default function TicketReopenReasonList() {
  const [projects, setProjects] = useState([]);

  // Fetch projects to populate the Project ID dropdown options
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketReopenReasonService, {
    idKey: 'reopenReasonID',
    itemName: 'Reopen Reason',
    initialSort: { key: 'reopenReasonID', direction: 'asc' },
  });

  // Schema configuration for Ticket_Reopen_Reason_TBL
  const reopenReasonFields = useMemo(() => [
    {
      name: 'reopenReasonID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'reasonText',
      label: 'Reason Text',
      type: 'varchar',
      required: true,
      maxLength: 255,
      placeholder: 'Enter reason why ticket is reopened...',
    },
    {
      name: 'isMandatoryFlag',
      label: 'Is Mandatory Flag',
      type: 'checkbox',
      required: false,
      placeholder: 'Flag whether selecting this reason is mandatory',
      defaultValue: true,
    },
  ], [projects]);

  const columns = [
    { key: 'reopenReasonID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const proj = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return proj ? proj.projectName : `Project #${row.projectID}`;
      },
    },
    { key: 'reasonText', label: 'Reason Text', sortable: true },
    {
      key: 'isMandatoryFlag',
      label: 'Is Mandatory',
      sortable: true,
      render: (row) => {
        const isMand = row.isMandatoryFlag === true || String(row.isMandatoryFlag) === 'true';
        return (
          <StatusBadge
            value={isMand ? 'Yes' : 'No'}
            customClass={isMand ? 'badge-danger' : 'badge-info'}
          />
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Reopen Reasons</h1>
          <div className="subtitle">
            Configure reasons required for reopening closed tickets per project.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Reopen Reason
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="reopenReasonID"
        emptyMessage="No reopen reasons found."
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        sortConfig={sortConfig}
        onSort={handleSort}
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {isFormOpen && (
        <TableForm
          fields={reopenReasonFields}
          initialValues={selectedRecord || { projectID: '', reasonText: '', isMandatoryFlag: true }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Reopen Reason"
        />
      )}

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Reopen Reason"
        itemLabel={selectedRecord ? selectedRecord.reasonText : 'Reopen Reason'}
        deleting={deleting}
      />
    </div>
  );
}
